"use client";

import React from "react";

import { useTranslations } from "next-intl";

import {
  PlanDetailCharacteristics,
  PlanDetailSocial,
} from "@/components/widgets/PlanDetail";
import { PlanDetailCompletedDate } from "@/components/widgets/PlanDetailCompletedDate";
import PlanRecommendations from "@/components/widgets/PlanRecommendations";
import { IImagesProperty } from "@/types/images.types";
import { ConvertedPrices } from "@/types/pricing.types";
import { Breadcrumbs } from "@/ui/Breadcrumbs";
import { AmountVariant } from "@/ui/Common/Amount";
import LocationTitle from "@/ui/Common/LocationTitle";
import { Divider } from "@/ui/Divider";
import { ObjectDetailHeader } from "@/widgets/ObjectDetail/Header";
import { ObjectDetailSlider } from "@/widgets/ObjectDetail/Slider";
import { PlanDetailPricing } from "@/widgets/PlanDetail/Pricing";
import { SalesDepartment } from "@/widgets/Property/SalesDepartment";

interface Layout {
  _id: string;
  completionQuarter?: number;
  completionYear?: number;
  coverImage?: string;
  additionalImages?: string[];
  rooms?: number;
  area?: number;
  type?: string;
  pricePerSqM?: number;
  convertedPrices?: ConvertedPrices;
  convertedPricesPerSqM?: ConvertedPrices;
  baseCurrency?: string;
  status?: string;
  lastOptimized?: string;
  lastCurrencyUpdate?: string;
  images: IImagesProperty[];
}

interface Breadcrumb {
  label: string;
  link?: string;
}

interface PlanDetailProps {
  layout: Layout;
  propertyData?: any;
  recommendedList?: any[]; // Assuming this is an array of recommended items
  manager?: any;
  planId: string;
  rooms?: number;
  area?: number;
  locale: string;
  slug: string;
  breadcrumbs?: Breadcrumb[];
}

export default function PlanDetail({
  layout,
  propertyData,
  recommendedList,
  manager,
  rooms,
  planId,
  locale,
  slug,
  breadcrumbs,
}: PlanDetailProps) {
  const t = useTranslations();

  const title = React.useMemo(() => {
    const roomsValue = rooms ?? layout.rooms;

    if (roomsValue == null || roomsValue === undefined) {
      return t("Property plan");
    }

    return t("pluralization.roomPlan", { count: roomsValue });
  }, [rooms, layout.rooms, t]);

  const completedDate = {
    quartal:
      layout.completionQuarter ||
      propertyData?.projectInfo?.completionQuarter ||
      1,
    year:
      layout.completionYear ||
      propertyData?.projectInfo?.completionYear ||
      new Date().getFullYear(),
  };
  
  const images = [...layout.images, propertyData.images[0]].filter(Boolean)

  return (
    <>
      <div className="flex flex-col gap-[4rem] lg:grid lg:grid-cols-2 xl:grid-cols-[3fr_2fr] 2xl:gap-[6rem]">
        <ObjectDetailSlider
          className={"xl:!max-w-full"}
          data={images}
          title={title || ""}
        />

        <div className={"flex flex-col gap-6"}>
          {breadcrumbs && breadcrumbs.length > 0 && (
            <Breadcrumbs items={breadcrumbs} />
          )}

          <ObjectDetailHeader
            title={title}
            objectId={propertyData?._id}
          />

          <div className="flex gap-8 items-start">
            <LocationTitle
              country={propertyData?.location?.country}
              region={propertyData?.location?.region}
              city={propertyData?.location?.city}
              district={propertyData?.location?.district}
              microdistrict={propertyData?.location?.microdistrict}
              street={propertyData?.location?.street}
              number={propertyData?.location?.number}
            />

            <PlanDetailCompletedDate
              className="mt-px"
              quartal={completedDate?.quartal}
              year={completedDate?.year}
            />
          </div>

          <PlanDetailPricing
            pricing={{
              convertedPrices: layout?.convertedPrices,
              convertedPricesPerSqM: layout?.convertedPricesPerSqM,
            }}
            showVariants={[AmountVariant.FULL, AmountVariant.PER_SQUARE]}
          />

          <Divider className="my-4" />

          <SalesDepartment
            name={manager?.name}
            photo={manager?.image}
            role={t("Manager")}
          />
        </div>
        <PlanDetailCharacteristics
          layoutData={layout}
          propertyData={propertyData}
        />

        <PlanDetailSocial
          id={propertyData?._id}
          title={title}
          className="mt-8 lg:mt-[67px]"
        />
      </div>
      {recommendedList && recommendedList.length > 0 && (
        <PlanRecommendations
          recommended={recommendedList}
          recommendedLink={encodeURI(
            `/developers/${slug}/plans?rooms[]=${layout.rooms || rooms}`,
          )}
          locale={locale}
          slug={slug}
          propertyData={propertyData}
          completedDate={completedDate}
          className={"xl:col-span-2 mt-[4rem] 2xl:mt-[6rem]"}
        />
      )}
    </>
  );
}
